import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { GameHandle } from '@juegazo/game-core';
import { createGame, SystemClock, SystemRng } from '@juegazo/game-core';
import { createContentProvider } from '@juegazo/content';
import {
  DEFAULT_ROOM_CONFIG,
  type GameStateSnapshot,
  type Mechanic,
  type PlayerId,
  type ReturnTarget,
  type RoomConfig,
  type VoteChoice,
} from '@juegazo/shared';
import './background';
import './vote-modal';

const MECHANIC_LABELS: Record<Mechanic, string> = {
  category: 'Categoría',
  starts_with_letter: 'Comienza por',
  contains: 'Contiene',
  excludes_letter: 'No contienen',
  chain: 'Inicia con la última letra',
  dual_condition: 'Dos condiciones',
  name_three: 'Nombra 3',
};
import { audioManager } from '../lib/audio-manager';

const LOCAL_RESULT_KEY = 'juegazo.localFinalResult';
const LOCAL_GAME_STATE_KEY = 'juegazo.localGameState';

interface LocalGameSave {
  snapshot: GameStateSnapshot;
  scores: { playerId: PlayerId; displayName: string; totalMs: number; explosions: number }[];
  savedAt: number;
}

const isLocalGameSave = (v: unknown): v is LocalGameSave => {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    'snapshot' in o && o.snapshot !== null && typeof o.snapshot === 'object' &&
    'scores' in o && Array.isArray(o.scores) &&
    'savedAt' in o && typeof o.savedAt === 'number'
  );
};

const loadSavedFinalResult = (): import('@juegazo/shared').FinalResult | null => {
  try {
    const raw = localStorage.getItem(LOCAL_RESULT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      'ranking' in parsed &&
      Array.isArray((parsed as { ranking: unknown }).ranking)
    ) {
      return parsed as import('@juegazo/shared').FinalResult;
    }
  } catch {
    // ignore
  }
  return null;
};

const saveFinalResult = (result: import('@juegazo/shared').FinalResult): void => {
  try {
    localStorage.setItem(LOCAL_RESULT_KEY, JSON.stringify(result));
  } catch {
    // ignore
  }
};

const clearSavedFinalResult = (): void => {
  try {
    localStorage.removeItem(LOCAL_RESULT_KEY);
  } catch {
    // ignore
  }
};

const loadSavedGameState = (): LocalGameSave | null => {
  try {
    const raw = localStorage.getItem(LOCAL_GAME_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (isLocalGameSave(parsed)) return parsed;
  } catch {
    // ignore
  }
  return null;
};

const clearSavedGameState = (): void => {
  try {
    localStorage.removeItem(LOCAL_GAME_STATE_KEY);
  } catch {
    // ignore
  }
};

interface LocalSnapshot extends GameStateSnapshot {
  localPlayers: string[];
  activeIndex: number;
}

/**
 * Da el displayName del jugador que recibirá la bomba en el próximo pase,
 * siguiendo la rotación ALEATORIA real del motor (rotationOrder), no el
 * orden secuencial de registro. Salta al jugador que ya recibió la bomba por
 * devolución forzada (lastForcedPassTarget), replicando el motor.
 */
export const computeNextAfterPass = (state: GameStateSnapshot): string => {
  const players = state.players ?? [];
  const order = state.rotationOrder ?? [];
  const forced = state.lastForcedPassTarget ?? null;
  const curId = state.currentPlayerId ?? '';
  if (order.length < 2) return '';
  let idx = order.indexOf(curId);
  if (idx === -1) idx = state.rotationIndex ?? 0;
  for (let steps = 0; steps < order.length + 2; steps++) {
    idx = (idx + 1) % order.length;
    const cand = order[idx];
    if (forced && cand === forced) continue;
    const p = players.find((x) => x.id === cand);
    if (p) return p.displayName;
  }
  return '';
};

@customElement('jgz-local')
export class JgzLocal extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: Array }) playerNames: string[] = [];
  @property({ type: Object }) config: RoomConfig | null = null;
  @state() private state: LocalSnapshot | null = null;
  @state() private showChangeConfirm = false;
  @state() private showEndConfirm = false;
  @state() private soundOn = audioManager.isEnabled();
  @state() private flashMessage: string | null = null;
  @state() private passToast: string | null = null;
  @state() private finalResult: import('@juegazo/shared').FinalResult | null = null;
  @state() private savedGame: LocalGameSave | null = null;

  private game: GameHandle | null = null;
  private tickTimer: number | null = null;
  private passToastTimer: number | null = null;
  private initialized = false;
  private lastActiveIndex = -1;

  connectedCallback(): void {
    super.connectedCallback();
    audioManager.unlock();
    this.soundOn = audioManager.isEnabled();
    this.addEventListener('jgz-vote-cast', this.onVoteCast as EventListener);
    this.addEventListener('jgz-vote-decide', this.onVoteDecide as EventListener);
    this.addEventListener('jgz-vote-clear', this.onVoteClear);
    const saved = loadSavedFinalResult();
    if (saved) {
      this.finalResult = saved;
      this.initialized = true;
      return;
    }
    const savedGame = loadSavedGameState();
    if (savedGame && savedGame.snapshot.state !== 'finished') {
      this.savedGame = savedGame;
      this.initialized = true;
      return;
    }
    if (!this.initialized && this.playerNames.length >= 2) this.initialize();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('jgz-vote-cast', this.onVoteCast as EventListener);
    this.removeEventListener('jgz-vote-decide', this.onVoteDecide as EventListener);
    this.removeEventListener('jgz-vote-clear', this.onVoteClear);
    if (this.tickTimer !== null) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
    if (this.passToastTimer !== null) {
      clearTimeout(this.passToastTimer);
      this.passToastTimer = null;
    }
    audioManager.teardown();
    this.game = null;
    this.initialized = false;
  }

  willUpdate(changed: Map<string, unknown>) {
    if (changed.has('playerNames') && !this.initialized && this.playerNames.length >= 2) {
      this.initialize();
    }
  }

  private initialize(): void {
    if (this.initialized) return;
    const cfg = this.config ?? DEFAULT_ROOM_CONFIG;
    const game = createGame({
      roomId: 'local',
      totalBombs: cfg.totalBombs,
      enabledMechanics: cfg.enabledMechanics,
      mixedMode: cfg.mixedMode,
      baseDurationMs: cfg.baseDurationMs,
      rng: new SystemRng(),
      clock: new SystemClock(),
      contentProvider: createContentProvider(),
    });
    for (let i = 0; i < this.playerNames.length; i++) {
      game.addPlayer(`p${i + 1}`, this.playerNames[i]);
    }
    game.start();
    game.startBomb();
    this.game = game;
    this.initialized = true;
    this.sync();
    this.persistGameState();

    this.tickTimer = window.setInterval(() => {
      if (!this.game) return;
      this.game.tick();
      this.sync();
    }, 100);
  }

  private persistGameState(): void {
    if (!this.game) return;
    try {
      const state = this.game.getState();
      const scores = this.game.getScores();
      const save: LocalGameSave = {
        snapshot: state,
        scores,
        savedAt: Date.now(),
      };
      localStorage.setItem(LOCAL_GAME_STATE_KEY, JSON.stringify(save));
    } catch {
      // ignore
    }
  }

  private sync(): void {
    if (!this.game) return;
    const peeked = this.game.peek();
    const snap = this.game.getState();
    const m = (snap.currentPlayerId ?? '').match(/^p(\d+)$/);
    const idx = m ? Math.max(0, parseInt(m[1], 10) - 1) : 0;
    this.state = {
      ...snap,
      remainingTimeMs: peeked.remainingTimeMs,
      paused: peeked.paused,
      state: peeked.state as GameStateSnapshot['state'],
      localPlayers: [...this.playerNames],
      activeIndex: idx,
    };
  }

  private get activePlayerName(): string {
    if (!this.state) return '';
    return this.playerNames[this.state.activeIndex] ?? '';
  }

  private get nextPlayerName(): string {
    if (!this.state || this.playerNames.length < 2) return '';
    return computeNextAfterPass(this.state);
  }

  private passBomb = () => {
    if (!this.game || !this.state) return;
    if (this.state.paused) return;
    if (this.state.state !== 'playing') return;
    this.game.passBomb();
    const nextName = this.nextPlayerName;
    this.passToast = `Pásala a ${nextName}`;
    if (this.passToastTimer !== null) clearTimeout(this.passToastTimer);
    this.passToastTimer = window.setTimeout(() => {
      this.passToast = null;
      this.passToastTimer = null;
    }, 2200);
    this.sync();
    this.persistGameState();
  };

  private returnBomb = () => {
    if (!this.game || !this.state) return;
    if (this.state.paused) return;
    try {
      this.game.returnBomb();
      this.sync();
      this.persistGameState();
    } catch {
      // window may have expired
    }
  };

  private resumeGame = () => {
    if (!this.game) return;
    this.game.resume();
    this.sync();
    this.persistGameState();
  };

  private pauseGame = () => {
    if (!this.game) return;
    this.game.pause();
    this.sync();
    this.persistGameState();
  };

  private advanceFromExplosion = () => {
    if (!this.game || !this.state) return;
    this.game.advanceBetweenBombs();
    this.sync();
    const after = this.game.peek();
    if (after.state === 'between_bombs') {
      this.game.startBomb();
      this.sync();
    } else if (after.state === 'finished') {
      this.finalResult = this.game.getFinalResult();
      saveFinalResult(this.finalResult);
      clearSavedGameState();
    } else if (after.state === 'tiebreak_ready') {
      // wait for tiebreak action
    }
    this.persistGameState();
  };

  private callVote = () => {
    if (!this.game || !this.state) return;
    try {
      this.game.startVote(this.state.currentPlayerId ?? '');
      this.sync();
      this.persistGameState();
    } catch {
      // ignore — game-core validates (paused + prior pass required)
    }
  };

  private onVoteCast = (e: CustomEvent<{ choice: VoteChoice; playerId?: string }>) => {
    if (!this.game || !this.state) return;
    // Hot-seat: the modal lets the user pick which player is casting. Fall back
    // to the current player when the event carries no explicit voter.
    const voterId = e.detail.playerId ?? this.state.currentPlayerId ?? '';
    if (!voterId) return;
    try {
      this.game.castVote(voterId, e.detail.choice);
      this.sync();
      this.persistGameState();
    } catch {
      // ignore
    }
  };

  private onVoteDecide = (e: CustomEvent<{ target: ReturnTarget }>) => {
    if (!this.game || !this.state) return;
    try {
      this.game.decideBombReturn(this.state.currentPlayerId ?? '', e.detail.target);
      this.sync();
      this.persistGameState();
    } catch {
      // ignore
    }
  };

  private onVoteClear = () => {
    if (!this.game || !this.state) return;
    try {
      this.game.clearVote();
      this.sync();
      this.persistGameState();
    } catch {
      // ignore
    }
  };

  private startTiebreak = () => {
    if (!this.game) return;
    try {
      this.game.startTiebreak();
      this.sync();
      this.persistGameState();
    } catch {
      // ignore
    }
  };

  private leaveLocal = () => {
    this.finalResult = null;
    this.savedGame = null;
    clearSavedFinalResult();
    clearSavedGameState();
    this.dispatchEvent(new CustomEvent('jgz-leave', { bubbles: true, composed: true }));
  };

  private startNewFromInterrupted = () => {
    this.savedGame = null;
    clearSavedGameState();
    this.initialized = false;
    this.initialize();
  };

  private leaveFromInterrupted = () => {
    this.savedGame = null;
    clearSavedGameState();
    this.dispatchEvent(new CustomEvent('jgz-leave', { bubbles: true, composed: true }));
  };

  private toggleSound = () => {
    this.soundOn = !this.soundOn;
    audioManager.setEnabled(this.soundOn);
  };

  updated(changed: Map<string, unknown>) {
    super.updated(changed);
    if (!this.state) return;

    audioManager.sync({
      state: this.state.state,
      paused: this.state.paused,
      tensionState: this.state.tensionState,
      bombNumber: this.state.bombNumber,
      remainingTimeMs: this.state.remainingTimeMs,
    });

    if (this.state.activeIndex !== this.lastActiveIndex) {
      const next = this.state.activeIndex;
      this.lastActiveIndex = next;
      requestAnimationFrame(() => {
        const el = this.querySelector('.player-name-anim');
        if (el instanceof HTMLElement) {
          el.animate?.(
            [
              { transform: 'scale(0.7)', opacity: '0.4' },
              { transform: 'scale(1.22)', opacity: '1', offset: 0.45 },
              { transform: 'scale(0.94)', offset: 0.7 },
              { transform: 'scale(1)' },
            ],
            { duration: 750, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
          );
        }
      });
    }
    void changed;
  }

  render() {
    if (this.finalResult) {
      return this.renderResults();
    }
    if (this.savedGame) {
      return this.renderInterrupted();
    }
    if (!this.state) {
      return this.renderInitializing();
    }
    if (this.state.state === 'tiebreak_ready') {
      return this.renderTiebreakPrompt();
    }
    if (this.state.state === 'exploded' && this.state.lastExplosionPlayerId) {
      return this.renderExplosion();
    }
    return this.renderPlaying();
  }

  private renderInitializing() {
    return html`
      <jgz-background></jgz-background>
      <div class="container fade-in" style="max-width: 480px; margin: 0 auto; text-align: center; position: relative; z-index: 1;">
        <div class="stack" style="gap: 1rem; margin-top: 4rem;">
          <div style="font-size: 3.5rem;">⏳</div>
          <p>Inicializando juego local…</p>
          <p class="muted">Si esto tarda demasiado, vuelve al inicio y configura la partida de nuevo.</p>
          <button
            class="btn-pill gray"
            data-testid="local-stuck-leave"
            @click=${this.leaveLocal}
            style="width: 100%;"
          >
            <span class="label">VOLVER AL INICIO</span>
          </button>
        </div>
      </div>
    `;
  }

  private renderPlaying() {
    const s = this.state!;
    const inminent = s.tensionState === 'inminente';
    const acelerado = s.tensionState === 'acelerado';
    const paused = s.paused;
    const segundos = Math.max(0, s.remainingTimeMs / 1000);
    const tensionClass = inminent ? 'tension-inminente' : acelerado ? 'tension-acelerado' : 'tension-normal';
    const canReturn = s.returnWindowMsRemaining > 0;
    const passLabel = paused ? 'Pausado' : 'Pasar turno';

    return html`
      <jgz-background></jgz-background>
      <div class="game-layout fade-in app-shell ${tensionClass}" style="position: relative; z-index: 1;">
        <div class="stack">
          ${this.renderHeader(s, segundos, inminent)}
          ${this.renderConsignaCard(s)}
          ${this.renderTurnCard(s)}
          ${this.renderBombCircle(s)}
          ${this.renderActionButtons(paused, canReturn, passLabel)}
        </div>
      </div>
      ${this.passToast ? this.renderPassToast() : nothing}
      ${this.showChangeConfirm ? this.renderChangeConfirm() : nothing}
      ${this.showEndConfirm ? this.renderEndConfirm() : nothing}
      ${this.state!.voting
        ? html`<jgz-vote-modal
            .voting=${this.state!.voting}
            .myPlayerId=${this.state!.currentPlayerId ?? ''}
            .isAdmin=${false}
            .isLocalMode=${true}
            .currentPlayerId=${this.state!.currentPlayerId ?? ''}
            .players=${this.state!.players}
          ></jgz-vote-modal>`
        : nothing}
      ${this.flashMessage ? this.renderFlash() : nothing}
    `;
  }

  private renderHeader(s: LocalSnapshot, segundos: number, inminent: boolean) {
    return html`
      <div class="row header-row" style="justify-content: space-between; flex-wrap: wrap; gap: 0.4rem; flex-shrink: 0;">
        <div class="juegazo-logo-wrap">
          <div class="juegazo-logo" style="font-family: 'Bungee', 'Impact', var(--font); letter-spacing: 0.04em; background: linear-gradient(135deg, var(--yellow) 0%, var(--orange) 100%); -webkit-background-clip: text; background-clip: text; color: transparent; line-height: 1; font-weight: 900;">¡JUEGAZO!</div>
        </div>
        <div class="row" style="gap: 0.4rem; align-items: center;">
          <button class="icon-btn-circle sound-toggle ${this.soundOn ? '' : 'muted'}" @click=${this.toggleSound} title=${this.soundOn ? 'Silenciar' : 'Activar sonido'}>
            ${this.soundOn
              ? html`<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 5L6 9H2v6h4l5 4V5zM15 8a4 4 0 0 1 0 8M19 5a8 8 0 0 1 0 14"/></svg>`
              : html`<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 5L6 9H2v6h4l5 4V5zM23 9l-6 6M17 9l6 6"/></svg>`}
          </button>
          <button class="icon-btn-circle primary" @click=${this.leaveLocal}>
            <span>Salir</span>
          </button>
        </div>
      </div>
      <div class="row" style="gap: 0.4rem; align-items: center; flex-shrink: 0; justify-content: center;">
        <div class="chip" style="background: rgba(108, 92, 231, 0.28); border-color: rgba(108, 92, 231, 0.5);">
          <div class="chip-icon-circle" style="color: var(--purple-2);">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 21h10M9 21v-4M15 21v-4M6 4h12v6a6 6 0 1 1-12 0V4z"/><path d="M6 4h12M4 4h16"/></svg>
          </div>
          <span>Ronda <strong>${s.currentRound}</strong></span>
        </div>
        <div class="chip" style="background: rgba(0, 184, 148, 0.28); border-color: rgba(0, 184, 148, 0.5);">
          <div class="chip-icon-circle" style="color: var(--teal-2);">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M9 9h.01M15 9h.01M9 15c.85.6 1.85 1 3 1s2.15-.4 3-1"/></svg>
          </div>
          <span>Juego <strong>${s.bombNumber}</strong> / ${s.totalBombs}</span>
        </div>
      </div>
    `;
  }

  private renderConsignaCard(s: LocalSnapshot) {
    const mechanic = s.selectedMechanic;
    return html`
      <div class="card" style="background: linear-gradient(135deg, rgba(0, 184, 148, 0.32), rgba(0, 184, 148, 0.18)); border-color: rgba(0, 184, 148, 0.5); position: relative; overflow: hidden; flex-shrink: 0;">
        <div class="consigna-content" style="text-align: center;">
          ${mechanic
            ? html`<div class="mechanic-subtitle">${MECHANIC_LABELS[mechanic]}</div>`
            : nothing}
          ${s.promptLines.map(
            (line) => html`<p class="consigna-prompt">${line}</p>`,
          )}
        </div>
      </div>
    `;
  }

  private renderTurnCard(s: LocalSnapshot) {
    return html`
      <div class="card" style="background: linear-gradient(135deg, rgba(108, 92, 231, 0.32), rgba(108, 92, 231, 0.18)); border-color: rgba(108, 92, 231, 0.5); flex-shrink: 0;">
        <div class="row" style="gap: 0.85rem; align-items: center;">
          <div class="card-icon" style="background: rgba(255,255,255,0.12); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: var(--purple-2);">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M4 21v-1a8 8 0 0 1 16 0v1"/></svg>
          </div>
          <div style="flex: 1;">
            <div class="section-label">TURNO DE</div>
            <div
              class="player-name-anim player-name"
              style="display: inline-block; transform-origin: center;"
            >
              ${this.activePlayerName}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderBombCircle(s: LocalSnapshot) {
    const inminent = s.tensionState === 'inminente';
    const acelerado = s.tensionState === 'acelerado';
    const ringClassExtra = inminent ? 'pulse-ring-fast' : '';
    const ringColor = inminent ? 'var(--red)' : acelerado ? 'var(--yellow)' : 'var(--teal)';
    const paused = s.paused;
    const emoji = s.paused ? '✋' : '💣';
    const emojiLabel = s.paused ? 'EN PAUSA' : 'TENGO LA BOMBA';
    return html`
      <div class="bomb-container">
        <div class="dots-left"></div>
        <div class="dots-right"></div>
        <div class="bomb-stage" style="position: relative; display: flex; align-items: center; justify-content: center;">
          <svg width="100%" height="100%" viewBox="0 0 240 240" preserveAspectRatio="xMidYMid meet" style="position: absolute; top: 0; left: 0; overflow: visible;">
            <defs>
              <linearGradient id="local-bomb-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stop-color="var(--teal)"/>
                <stop offset="50%" stop-color="var(--yellow)"/>
                <stop offset="100%" stop-color="var(--red)"/>
              </linearGradient>
            </defs>
            <circle
              class="pulse-ring-base ${ringClassExtra}"
              cx="120" cy="120" r="105"
              stroke="url(#local-bomb-grad)"
              stroke-width="12"
              style="--ring-color: ${ringColor};"
            />
            <circle
              class="pulse-ring-pulse ${ringClassExtra}"
              cx="120" cy="120" r="105"
              stroke-dasharray="90 555"
              stroke-width="12"
              style="--ring-color: ${ringColor};"
            />
          </svg>
          <div
            class="heartbeat ${inminent ? 'heartbeat-fast' : ''} ${inminent ? 'bomb-shake' : ''}"
            style="position: relative; z-index: 2; user-select: none;"
          >
            <div class="bomb-emoji">${emoji}</div>
          </div>
        </div>
        ${paused
          ? html`<p class="center-label muted-text" style="margin: 0;">Juego en pausa</p>`
          : html`<p class="center-label muted-text" style="margin: 0;">${emojiLabel}</p>`}
      </div>
    `;
  }

  private renderActionButtons(paused: boolean, canReturn: boolean, passLabel: string) {
    const pauseLabel = paused ? 'REANUDAR' : 'PAUSA';
    return html`
      <div class="row" style="gap: 0.6rem; flex-wrap: wrap; flex-shrink: 0; align-items: stretch;">
        <button class="btn-pill green" ?disabled=${paused} @click=${this.passBomb}>
          <div class="ico">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M5 4l14 8-14 8V4z"/></svg>
          </div>
          <span class="label">PASAR</span>
          <span class="sublabel">TURNO A OTRO JUGADOR</span>
        </button>
        <button class="btn-pill gray" @click=${paused ? this.resumeGame : this.pauseGame}>
          <div class="ico">
            ${paused
              ? html`<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M5 4l14 8-14 8V4z"/></svg>`
              : html`<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>`}
          </div>
          <span class="label">${pauseLabel}</span>
          <span class="sublabel">juego</span>
        </button>
      </div>
      <div class="row" style="flex-shrink: 0;">
        <button class="btn-pill primary" style="flex: 1;" ?disabled=${!canReturn || paused} @click=${this.returnBomb}>
          <div class="ico">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M3 11l9-7 9 7v10a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-5h-2v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V11z" transform="scale(-1 1) translate(-24 0)"/></svg>
          </div>
          <span class="label">DEVOLVER</span>
          <span class="sublabel">turno</span>
        </button>
      </div>
      ${paused && !this.state!.voting
        ? html`
            <button class="btn-pill gray btn-action-menu" @click=${this.callVote} style="font-size: 1rem;">
              <span class="label" style="font-size: 1rem;">LLAMAR A VOTACIÓN</span>
            </button>
          `
        : nothing}
    `;
  }

  private renderPassToast() {
    return html`
      <div
        style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 200; pointer-events: none;"
      >
        <div
          class="card fade-in"
          style="display: flex; flex-direction: column; align-items: center; gap: 0.5rem; padding: 1.25rem 1.75rem; text-align: center; background: rgba(15, 12, 30, 0.95); border-color: var(--yellow); box-shadow: 0 10px 32px rgba(0,0,0,0.6);"
        >
          <div style="font-size: 2rem;">📱</div>
          <div style="font-size: 1rem; letter-spacing: 0.18em; font-weight: 700; color: var(--yellow);">${this.passToast?.toUpperCase()}</div>
        </div>
      </div>
    `;
  }

  private renderExplosion() {
    const exploder = this.state?.players.find((p) => p.id === this.state?.lastExplosionPlayerId);
    const name = exploder?.displayName ?? 'alguien';
    const isLastBomb = (this.state?.bombNumber ?? 0) >= (this.state?.totalBombs ?? 0);
    return html`
      <div class="container fade-in" style="text-align: center; padding-top: 4rem;">
        <div class="stack">
          <div style="font-size: 7rem; line-height: 1;">💥</div>
          <h1 style="font-size: 2rem; margin: 0;">¡La bomba explotó!</h1>
          <p style="font-size: 1.1rem; color: var(--muted); margin: 0;">
            A <strong style="color: var(--fg);">${name}</strong> le explotó la bomba.
          </p>
          <div class="card" style="background: rgba(255, 255, 255, 0.04); margin-top: 1.5rem; display: flex; flex-direction: column; align-items: center; gap: 0.6rem; padding: 0.6rem;">
            <p class="muted" style="margin: 0; text-align: center;">
              ${isLastBomb
                ? html`Última bomba completada.`
                : html`Toca el botón para iniciar la siguiente bomba.`}
            </p>
            <button class="btn-pill primary" @click=${this.advanceFromExplosion}>
              <span class="label">${isLastBomb ? '🏁 VER RESULTADOS' : '▶ SIGUIENTE BOMBA'}</span>
              <span class="sublabel">${isLastBomb ? 'Final de la partida' : `Bomba ${(this.state?.bombNumber ?? 0) + 1} / ${this.state?.totalBombs}`}</span>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderTiebreakPrompt() {
    const tied = this.state?.tiebreakPlayers ?? [];
    const names = tied
      .map((id) => this.state?.players.find((p) => p.id === id)?.displayName)
      .filter((n): n is string => typeof n === 'string');
    return html`
      <div class="container fade-in" style="text-align: center; padding-top: 4rem;">
        <div class="stack">
          <div style="font-size: 5rem;">🤝</div>
          <h1 style="font-size: 2rem; margin: 0;">¡Hubo empate!</h1>
          <p class="muted" style="margin: 0;">Los jugadores con el mismo mejor tiempo deben desempatar.</p>
          <div class="card" style="background: rgba(255, 255, 255, 0.04); margin-top: 1.5rem;">
            <p style="margin: 0 0 0.85rem; font-size: 1rem; letter-spacing: 0.18em; color: var(--muted);">DESEMPATAN</p>
            <p style="font-size: 1.4rem; font-weight: 800; margin: 0 0 1rem;">${names.join(' · ')}</p>
            <button class="btn-pill primary" @click=${this.startTiebreak}>
              <span class="label">▶ INICIAR DESEMPATE</span>
              <span class="sublabel">Solo jugarán los empatados</span>
            </button>
            <button class="btn-pill gray" @click=${this.leaveLocal} style="margin-top: 0.6rem;">
              <span class="label">SALIR</span>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderInterrupted() {
    if (!this.savedGame) return html``;
    const { snapshot, scores, savedAt } = this.savedGame;
    const elapsedMs = Date.now() - savedAt;
    const remainingMs = Math.max(0, snapshot.remainingTimeMs - elapsedMs);
    const activePlayer = snapshot.players.find((p) => p.id === snapshot.currentPlayerId);
    const activeName = activePlayer?.displayName ?? '—';
    const fmt = (ms: number) => {
      const s = Math.max(0, Math.floor(ms / 1000));
      return `${s}s`;
    };
    return html`
      <div class="container fade-in" style="max-width: 480px; margin: 0 auto;">
        <div class="stack" style="gap: 1rem;">
          <div class="card" style="text-align: center;">
            <div style="font-size: 3.5rem;">⏸️</div>
            <h2 style="margin: 0.4rem 0;">Tu partida fue interrumpida</h2>
            <p class="muted" style="margin: 0.4rem 0;">
              Estábamos en la bomba <strong>${snapshot.bombNumber}</strong> de
              <strong>${snapshot.totalBombs}</strong>.
            </p>
            <p class="muted" style="margin: 0.2rem 0;">
              Turno de <strong style="color: var(--fg);">${activeName}</strong>
              · ${fmt(remainingMs)} restantes
            </p>
          </div>
          <div class="card">
            <h3 style="margin: 0 0 0.6rem;">Puntajes hasta el momento</h3>
            <table style="width: 100%; border-collapse: collapse; font-size: 0.95rem;">
              <thead>
                <tr style="background: rgba(255,255,255,0.04);">
                  <th style="text-align: left; padding: 0.5rem;">Jugador</th>
                  <th style="text-align: right; padding: 0.5rem;">Tiempo</th>
                  <th style="text-align: right; padding: 0.5rem;">Penalización</th>
                  <th style="text-align: right; padding: 0.5rem;">💥</th>
                </tr>
              </thead>
              <tbody>
                ${scores.map(
                  (s, i) => html`
                    <tr style=${i % 2 === 0 ? 'background: rgba(255,255,255,0.02);' : ''}>
                      <td style="padding: 0.5rem;">${s.displayName}</td>
                      <td style="text-align: right; padding: 0.5rem; font-variant-numeric: tabular-nums;">${fmt(s.totalMs)}</td>
                      <td style="text-align: right; padding: 0.5rem; font-variant-numeric: tabular-nums; color: var(--muted);">${fmt(s.explosions * 5000)}</td>
                      <td style="text-align: right; padding: 0.5rem;">${s.explosions}</td>
                    </tr>
                  `,
                )}
              </tbody>
            </table>
            <p class="muted" style="margin: 0.5rem 0 0; font-size: 0.78rem; text-align: right;">
              Penalización: informativo, no influye en el ranking.
            </p>
          </div>
          <button
            class="btn-pill yellow"
            data-testid="interrupted-new-game"
            @click=${this.startNewFromInterrupted}
          >
            <span class="label">NUEVA PARTIDA</span>
            <span class="sublabel">Arranca desde la bomba 1</span>
          </button>
          <button
            class="btn-pill gray"
            data-testid="interrupted-leave"
            @click=${this.leaveFromInterrupted}
          >
            <span class="label">VOLVER AL INICIO</span>
          </button>
        </div>
      </div>
    `;
  }

  private renderResults() {
    if (!this.finalResult) return html``;
    const ranking = this.finalResult.ranking;
    const winnerId = this.finalResult.winnerId;
    const fmt = (ms: number) => {
      const s = Math.floor(ms / 1000);
      const m = Math.floor(s / 60);
      const r = s % 60;
      return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `${r}s`;
    };
    return html`
      <div class="container fade-in">
        <div class="stack">
          <div style="text-align: center;">
            <div style="font-size: 3.5rem;">🏆</div>
            <h1 style="font-size: 1.8rem; margin: 0;">Resultados finales</h1>
            ${winnerId
              ? html`<p class="chip" style="background: linear-gradient(135deg, var(--yellow), var(--orange)); color: #1F1F38; font-size: 1rem; padding: 0.7rem 1.3rem; margin: 1rem auto; font-weight: 800;">
                  🏆 ${ranking.find((r) => r.playerId === winnerId)?.displayName ?? winnerId} ganó
                </p>`
              : html`<p class="muted">No hay ganador declarado</p>`}
          </div>
          <div class="card" style="padding: 0; overflow: hidden;">
            <table style="width: 100%; border-collapse: collapse; font-size: 1rem;">
              <thead>
                <tr style="background: rgba(255,255,255,0.04);">
                  <th style="text-align: center; padding: 0.85rem; width: 50px;">#</th>
                  <th style="text-align: left; padding: 0.85rem;">Jugador</th>
                  <th style="text-align: right; padding: 0.85rem;">Tiempo</th>
                  <th style="text-align: right; padding: 0.85rem;">Penalización</th>
                  <th style="text-align: right; padding: 0.85rem;">💥</th>
                </tr>
              </thead>
              <tbody>
                ${ranking.map(
                  (entry, i) => html`
                    <tr style=${entry.playerId === winnerId
                      ? 'background: linear-gradient(90deg, rgba(253, 203, 110, 0.18), transparent);'
                      : i % 2 === 0
                        ? 'background: rgba(255,255,255,0.02);'
                        : ''}>
                      <td style="text-align: center; padding: 0.85rem; font-weight: 800; color: var(--muted);">${i + 1}</td>
                      <td style="padding: 0.85rem;">
                        ${entry.playerId === winnerId ? '🏆 ' : ''}${entry.displayName}
                      </td>
                      <td style="text-align: right; padding: 0.85rem; font-variant-numeric: tabular-nums;">${fmt(entry.totalMs)}</td>
                      <td style="text-align: right; padding: 0.85rem; font-variant-numeric: tabular-nums; color: var(--muted);">${fmt(entry.explosions * 5000)}</td>
                      <td style="text-align: right; padding: 0.85rem;">${entry.explosions}</td>
                    </tr>
                  `,
                )}
              </tbody>
            </table>
            <p class="muted" style="margin: 0.6rem 1rem 1rem; font-size: 0.82rem; text-align: right;">
              Penalización: informativo, no influye en el ranking.
            </p>
          </div>
          <button class="btn-pill primary" @click=${this.leaveLocal}>
            <span class="label">VOLVER AL INICIO</span>
          </button>
        </div>
      </div>
    `;
  }

  private renderChangeConfirm() {
    return html`
      <div style="position: fixed; inset: 0; background: rgba(5, 8, 17, 0.85); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 1.5rem;">
        <div class="card fade-in" style="max-width: 480px; width: 100%; background: var(--bg-2);">
          <h2>Cambiar consigna</h2>
          <p style="margin: 1rem 0;">No implementado en juego local.</p>
          <div class="row" style="justify-content: flex-end;">
            <button class="primary" @click=${() => (this.showChangeConfirm = false)}>Cerrar</button>
          </div>
        </div>
      </div>
    `;
  }

  private renderEndConfirm() {
    return html`
      <div style="position: fixed; inset: 0; background: rgba(5, 8, 17, 0.85); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 1.5rem;">
        <div class="card fade-in" style="max-width: 480px; width: 100%; background: var(--bg-2);">
          <h2>Terminar partida</h2>
          <p style="margin: 1rem 0;">No implementado en juego local.</p>
          <div class="row" style="justify-content: flex-end;">
            <button class="primary" @click=${() => (this.showEndConfirm = false)}>Cerrar</button>
          </div>
        </div>
      </div>
    `;
  }

  private renderFlash() {
    return html`
      <div
        style="position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); background: var(--yellow); color: #1F1F38; padding: 0.85rem 1.5rem; border-radius: 14px; box-shadow: var(--shadow); font-weight: 600; max-width: 90vw; z-index: 100; animation: fade-in 0.3s ease;"
      >
        ${this.flashMessage}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'jgz-local': JgzLocal;
  }
}
